import { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "@/context/UserContext";
import { needsFlexDemo } from "@/shared/constants/clientOnboarding.const";
import ProfileWelcomeCreditsModal from "@/pages/profile-verification/ProfileWelcomeCreditsModal";
import { PROFILE_WELCOME_CREDITS_KEY } from "@/shared/constants/onboarding.const";
import trpc from "@/shared/utils/trpc";
import { loadFlexDemoSession, saveFlexDemoSession } from "./flexDemo.session";
import { FlexDemoSessionContext } from "./FlexDemoSessionContext";
import FlexDemoCoach from "./FlexDemoCoach";

export function FlexDemoProvider({ children }: { children: React.ReactNode }) {
  const { userDetails } = useUser();
  const userId = userDetails?.id ?? "";
  const demoActive = needsFlexDemo(userDetails);
  const [session, setSession] = useState(() => loadFlexDemoSession(userId || "anon"));
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [signupBonusCollaborations, setSignupBonusCollaborations] = useState(0);
  const [signupBonusExpiryMinutes, setSignupBonusExpiryMinutes] = useState(129600);

  const persist = useCallback(
    (next: typeof session) => {
      setSession(next);
      if (userId) saveFlexDemoSession(userId, next);
    },
    [userId],
  );

  useEffect(() => {
    if (!userId) return;
    setSession(loadFlexDemoSession(userId));
  }, [userId]);

  useEffect(() => {
    if (!userId || !demoActive) return;
    const shouldShowWelcome =
      sessionStorage.getItem(PROFILE_WELCOME_CREDITS_KEY) === "1" && !session.welcomeDismissed;
    if (!shouldShowWelcome) return;
    void trpc.paymentConfig.get
      .query()
      .then((config) => {
        const bonus = config.signupBonusCollaborations ?? 0;
        setSignupBonusCollaborations(bonus);
        setSignupBonusExpiryMinutes(config.signupBonusExpiryMinutes ?? 129600);
        if (bonus <= 0) {
          sessionStorage.removeItem(PROFILE_WELCOME_CREDITS_KEY);
          persist({ ...session, welcomeDismissed: true });
          return;
        }
        setWelcomeOpen(true);
      })
      .catch(() => {
        sessionStorage.removeItem(PROFILE_WELCOME_CREDITS_KEY);
        persist({ ...session, welcomeDismissed: true });
      });
  }, [userId, demoActive, persist, session]);

  const dismissWelcome = useCallback(() => {
    sessionStorage.removeItem(PROFILE_WELCOME_CREDITS_KEY);
    persist({ ...session, welcomeDismissed: true });
    setWelcomeOpen(false);
  }, [persist, session]);

  const ctx = useMemo(
    () => ({ session, persist, userId, demoActive }),
    [session, persist, userId, demoActive],
  );

  return (
    <FlexDemoSessionContext.Provider value={ctx}>
      {children}
      {demoActive ? (
        <ProfileWelcomeCreditsModal
          opened={welcomeOpen}
          onClose={dismissWelcome}
          onContinue={dismissWelcome}
          signupBonusCollaborations={signupBonusCollaborations}
          signupBonusExpiryMinutes={signupBonusExpiryMinutes}
          verificationFeeCollaborations={0}
          verificationFeeCharged={false}
        />
      ) : null}
      {demoActive && !welcomeOpen ? <FlexDemoCoach /> : null}
    </FlexDemoSessionContext.Provider>
  );
}
